import { TableBody, TableCell, TableHead, TableRow } from '@mui/material'

import { StickyTable } from '../../common/StickyTable'
import { TableSkeleton } from '../../skeletons/TableSkeleton'

interface ClientLedgerListingProps {
  loading: boolean
  headers: string[]
  rows: Record<string, string>[]
}

/**
 * A row the mapper emitted as a subtotal or the grand total.
 *
 * `clientLedgerMapper` marks neither explicitly: a detail row always carries a Date, a subtotal
 * carries figures with no Date, and the grand total is the one whose Description says so. Matching
 * on that shape keeps the contract with the mapper in one place instead of spreading it over the
 * table body.
 */
const isTotalRow = (row: Record<string, string>) => !row?.Date

/**
 * The ledger table.
 *
 * Deliberately unpaginated: the mapper appends a subtotal per matter and a grand total after the
 * detail rows, so slicing the array into pages would strand those totals on whichever page they
 * happened to land on. A single matter's ledger is a scrollable-length list, not a listing.
 */
const ClientLedgerListing = ({ loading, headers, rows }: ClientLedgerListingProps) => {
  return (
    <StickyTable height="460px">
      <TableHead>
        <TableRow>
          {headers.map(columnName => (
            <TableCell key={columnName} align="left" sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>
              {columnName}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>

      {loading ? (
        <TableSkeleton noOfRows={7} noOfCols={headers.length || 8} />
      ) : (
        <TableBody>
          {rows.map((row, rowIndex) => {
            const isTotal = isTotalRow(row)

            return (
              <TableRow key={rowIndex} sx={isTotal ? { bgcolor: 'grey.50' } : undefined}>
                {headers.map(header => (
                  <TableCell
                    key={header}
                    align="left"
                    sx={{ whiteSpace: 'nowrap', fontWeight: isTotal ? 'bold' : 'normal' }}
                  >
                    {row[header] ?? ''}
                  </TableCell>
                ))}
              </TableRow>
            )
          })}
        </TableBody>
      )}
    </StickyTable>
  )
}

export default ClientLedgerListing
